import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Button, Space, Popconfirm, message, Tooltip, Descriptions, Empty, Select, Collapse, Modal, Grid } from 'antd';
import { PlayCircleOutlined, PauseCircleOutlined, ReloadOutlined, BarChartOutlined, EyeOutlined } from '@ant-design/icons';
import { metaAdsApi } from '../../utils/api';
import ColumnsCardList from '../common/ColumnsCardList';

/**
 * Liste des campagnes Meta créées depuis le backoffice, avec pilotage et performances.
 *
 * L'activation d'une campagne engage une dépense réelle : elle est protégée par une
 * confirmation explicite indiquant le budget journalier concerné.
 */

// Couleurs et libellés d'affichage des statuts
const STATUTS = {
  PAUSED: { color: 'orange', label: 'En pause' },
  ACTIVE: { color: 'green', label: 'En diffusion' },
  ERROR: { color: 'red', label: 'Échec' }
};

const MetaAdsCampaignsList = () => {
  // Mobile (< md) : cartes à la place du tableau ; `=== false` évite un flash avant la mesure
  const screens = Grid.useBreakpoint();
  const isMobile = screens.md === false;
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [updatingId, setUpdatingId] = useState(null);
  // Période utilisée pour les détails de performances chargés depuis Meta.
  const [period, setPeriod] = useState('last_30d');
  // Performances chargées à la demande, indexées par identifiant de campagne
  const [insights, setInsights] = useState({});
  // Performances détaillées, indexées par campagne, niveau et identifiant Meta.
  const [childInsights, setChildInsights] = useState({});
  const [childTargets, setChildTargets] = useState({});
  const [preview, setPreview] = useState(null);

  const chargerCampagnes = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await metaAdsApi.getCampaigns();
      setCampaigns(data.data || []);
    } catch (error) {
      console.error('Erreur lors du chargement des campagnes Meta:', error);
      message.error('Erreur lors du chargement des campagnes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    chargerCampagnes();
  }, [chargerCampagnes]);

  /**
   * Active ou met en pause une campagne sur Meta.
   * @param {Object} campagne
   * @param {string} statut - 'ACTIVE' ou 'PAUSED'
   */
  const changerStatut = async (campagne, statut) => {
    try {
      setUpdatingId(campagne.id);
      const { data } = await metaAdsApi.updateCampaignStatus(campagne.id, statut);
      message.success(data.message);
      setCampaigns(prev => prev.map(c => (c.id === campagne.id ? data.data : c)));
    } catch (error) {
      console.error('Erreur lors du changement de statut:', error);
      message.error(error.response?.data?.message || 'Erreur lors du changement de statut');
    } finally {
      setUpdatingId(null);
    }
  };

  /**
   * Charge les performances d'une campagne depuis Meta.
   * @param {Object} campagne
   */
  const chargerPerformances = useCallback(async (campagne, periodeDemandee = period) => {
    try {
      const { data } = await metaAdsApi.getCampaignInsights(campagne.id, { preset: periodeDemandee });
      setInsights(prev => ({ ...prev, [campagne.id]: data.data }));
      if (!data.data) message.info("Aucune donnée disponible pour cette campagne.");
    } catch (error) {
      console.error('Erreur lors du chargement des performances:', error);
      message.error(error.response?.data?.message || 'Erreur lors du chargement des performances');
    }
  }, [period]);

  /**
   * Charge à la demande les performances d'un ensemble ou d'une publicité.
   * Les données sont mémorisées afin que l'arborescence reste rapide à consulter.
   */
  const chargerPerformancesElement = useCallback(async (campagneId, type, elementId, periodeDemandee = period) => {
    const cle = `${campagneId}:${type}:${elementId}`;
    try {
      const appel = type === 'adset'
        ? metaAdsApi.getAdSetInsights(campagneId, elementId, { preset: periodeDemandee })
        : metaAdsApi.getAdInsights(campagneId, elementId, { preset: periodeDemandee });
      const { data } = await appel;
      setChildInsights(prev => ({ ...prev, [cle]: data.data }));
      setChildTargets(prev => ({ ...prev, [cle]: { campagneId, type, elementId } }));
    } catch (error) {
      console.error(`Erreur lors du chargement des performances ${type}:`, error);
      message.error(error.response?.data?.message || 'Erreur lors du chargement des performances détaillées');
    }
  }, [period]);

  /**
   * Recharge la liste, les statuts et chaque bloc de performances déjà ouvert.
   * Les statistiques restent ainsi cohérentes avec la période sélectionnée sans
   * obliger l'utilisateur à cliquer de nouveau sur chaque bouton « Performances ».
   */
  const actualiserCampagnesEtPerformances = async () => {
    await chargerCampagnes();
    await Promise.all(
      Object.keys(insights).map(id => chargerPerformances({ id }))
    );
    await Promise.all(
      Object.values(childTargets).map(target => chargerPerformancesElement(target.campagneId, target.type, target.elementId))
    );
  };

  /**
   * Applique une nouvelle période et recharge immédiatement les performances
   * déjà consultées avec cette période, afin d'éviter l'affichage d'un cache ancien.
   *
   * @param {string} nouvellePeriode - Preset Meta choisi dans le sélecteur
   */
  const changerPeriode = (nouvellePeriode) => {
    setPeriod(nouvellePeriode);
    Object.keys(insights).forEach(id => chargerPerformances({ id }, nouvellePeriode));
    Object.values(childTargets).forEach(target => (
      chargerPerformancesElement(target.campagneId, target.type, target.elementId, nouvellePeriode)
    ));
  };

  /**
   * Ouvre le visuel sauvegardé avec le brouillon et consulte Meta pour compléter
   * l'aperçu vidéo. L'appel vidéo est fait seulement quand l'utilisateur le demande.
   */
  const ouvrirApercu = async (campagne, adSetIndex, adIndex, ad) => {
    const brouillonAnnonce = campagne.draft?.adSets?.[adSetIndex]?.ads?.[adIndex] || null;
    setPreview({ ad, brouillonAnnonce, mediaMeta: null, loading: true });
    try {
      const { data } = await metaAdsApi.getAdPreview(campagne.id, ad.id);
      setPreview({ ad, brouillonAnnonce, mediaMeta: data.data, loading: false });
    } catch (error) {
      console.error('Erreur lors du chargement de l’aperçu Meta:', error);
      setPreview({ ad, brouillonAnnonce, mediaMeta: null, loading: false });
      message.warning('Aperçu Meta indisponible : les visuels enregistrés restent affichés.');
    }
  };

  /**
   * Affiche l'arborescence enregistrée lors de la publication et propose les
   * performances de chaque niveau sans déclencher de requête avant un clic.
   */
  const rendreStructureCampagne = (campagne) => {
    const adSets = campagne.meta_result?.adSets || [];
    if (!adSets.length) return null;

    return (
      <Collapse
        className="mt-3"
        size="small"
        items={adSets.map((adSet, adSetIndex) => ({
          key: adSet.id,
          label: `Ensemble : ${adSet.name || adSet.id}`,
          children: (
            <div className="space-y-3">
              <div className="flex items-center justify-between gap-3 text-xs text-gray-500">
                <span>ID Meta : {adSet.id}</span>
                <Button size="small" onClick={() => chargerPerformancesElement(campagne.id, 'adset', adSet.id)}>
                  Performances
                </Button>
              </div>
              {childInsights[`${campagne.id}:adset:${adSet.id}`] && (
                <Descriptions size="small" column={3} bordered>
                  <Descriptions.Item label="Dépense">{childInsights[`${campagne.id}:adset:${adSet.id}`].spend.toLocaleString()} {campagne.currency}</Descriptions.Item>
                  <Descriptions.Item label="Impressions">{childInsights[`${campagne.id}:adset:${adSet.id}`].impressions.toLocaleString()}</Descriptions.Item>
                  <Descriptions.Item label="Conversations">{childInsights[`${campagne.id}:adset:${adSet.id}`].conversations || 0}</Descriptions.Item>
                </Descriptions>
              )}
              {(adSet.ads || []).map((ad, adIndex) => {
                const cle = `${campagne.id}:ad:${ad.id}`;
                const statsAnnonce = childInsights[cle];
                return (
                  <div key={ad.id} className="rounded border border-gray-200 p-2">
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-sm">Publicité : {ad.name || ad.id}</span>
                      <Space size="small">
                        <Button size="small" icon={<EyeOutlined />} onClick={() => ouvrirApercu(campagne, adSetIndex, adIndex, ad)}>Aperçu</Button>
                        <Button size="small" onClick={() => chargerPerformancesElement(campagne.id, 'ad', ad.id)}>Performances</Button>
                      </Space>
                    </div>
                    <div className="mt-1 text-xs text-gray-500">ID Meta : {ad.id}</div>
                    {statsAnnonce && <div className="mt-2 text-xs">Dépense : {statsAnnonce.spend.toLocaleString()} {campagne.currency} · Impressions : {statsAnnonce.impressions.toLocaleString()} · Conversations : {statsAnnonce.conversations || 0}</div>}
                  </div>
                );
              })}
            </div>
          )
        }))}
      />
    );
  };

  const colonnes = [
    {
      title: 'Campagne',
      dataIndex: 'name',
      key: 'name',
      render: (nom, campagne) => (
        <div>
          <div className="font-medium">{nom}</div>
          <div className="text-xs text-gray-500">
            {campagne.draft?.objectiveLabel} · {campagne.draft?.adSets?.length || 0} ensemble(s)
          </div>
        </div>
      )
    },
    {
      title: 'Budget / jour',
      dataIndex: 'daily_budget',
      key: 'daily_budget',
      render: (budget, campagne) => `${Number(budget).toLocaleString()} ${campagne.currency}`
    },
    {
      title: 'Statut',
      dataIndex: 'status',
      key: 'status',
      render: (statut, campagne) => (
        <Tooltip title={campagne.error_message || null}>
          <Tag color={STATUTS[statut]?.color || 'default'}>
            {STATUTS[statut]?.label || statut}
          </Tag>
        </Tooltip>
      )
    },
    {
      title: 'Créée le',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString('fr-FR')
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, campagne) => (
        <Space>
          {campagne.status === 'PAUSED' && campagne.meta_campaign_id && (
            <Popconfirm
              title="Activer la diffusion ?"
              description={`La campagne commencera à dépenser ${Number(campagne.daily_budget).toLocaleString()} ${campagne.currency} par jour.`}
              okText="Activer"
              cancelText="Annuler"
              onConfirm={() => changerStatut(campagne, 'ACTIVE')}
            >
              <Button
                type="primary"
                size="small"
                icon={<PlayCircleOutlined />}
                loading={updatingId === campagne.id}
              >
                Activer
              </Button>
            </Popconfirm>
          )}

          {campagne.status === 'ACTIVE' && (
            <Button
              size="small"
              icon={<PauseCircleOutlined />}
              loading={updatingId === campagne.id}
              onClick={() => changerStatut(campagne, 'PAUSED')}
            >
              Mettre en pause
            </Button>
          )}

          {campagne.meta_campaign_id && (
            <Button
              size="small"
              icon={<BarChartOutlined />}
              onClick={() => chargerPerformances(campagne)}
            >
              Performances
            </Button>
          )}
        </Space>
      )
    }
  ];

  // Contenu déplié d'une campagne (performances + structure), partagé par le tableau et les cartes mobile
  const rendreDetailsCampagne = (campagne) => {
    const stats = insights[campagne.id];
    if (!stats) {
      return (
        <>
          <span className="text-gray-500 text-sm">Cliquez sur « Performances » pour charger les données depuis Meta.</span>
          {rendreStructureCampagne(campagne)}
        </>
      );
    }
    return (
      <>
        <Descriptions size="small" column={{ xs: 1, sm: 2, lg: 4 }} bordered>
          <Descriptions.Item label="Dépense">
            {stats.spend.toLocaleString()} {campagne.currency}
          </Descriptions.Item>
          <Descriptions.Item label="Impressions">{stats.impressions.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Portée">{(stats.reach || 0).toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="Clics">{stats.clicks.toLocaleString()}</Descriptions.Item>
          <Descriptions.Item label="CTR">{Number(stats.ctr || 0).toFixed(2)} %</Descriptions.Item>
          <Descriptions.Item label="CPC">
            {Number(stats.cpc || 0).toFixed(0)} {campagne.currency}
          </Descriptions.Item>
          <Descriptions.Item label="CPM">
            {Number(stats.cpm || 0).toFixed(0)} {campagne.currency}
          </Descriptions.Item>
          <Descriptions.Item label="Fréquence">{Number(stats.frequency || 0).toFixed(2)}</Descriptions.Item>
          <Descriptions.Item label="Conversations">{stats.conversations || 0}</Descriptions.Item>
          <Descriptions.Item label="Achats">{stats.purchases || 0}</Descriptions.Item>
          <Descriptions.Item label="Coût / conversation">
            {stats.costPerConversation === null ? '—' : `${Number(stats.costPerConversation).toFixed(0)} ${campagne.currency}`}
          </Descriptions.Item>
          <Descriptions.Item label="CPA">
            {stats.costPerPurchase === null ? '—' : `${Number(stats.costPerPurchase).toFixed(0)} ${campagne.currency}`}
          </Descriptions.Item>
          <Descriptions.Item label="ROAS">
            {stats.roas === null ? '—' : `${Number(stats.roas).toFixed(2)}×`}
          </Descriptions.Item>
        </Descriptions>
        {rendreStructureCampagne(campagne)}
      </>
    );
  };

  return (
    <Card
      title="Mes campagnes"
      className="shadow-sm"
      extra={
        <Space wrap>
          <Select
            value={period}
            onChange={changerPeriode}
            style={{ width: 165 }}
            options={[
              { value: 'today', label: "Aujourd'hui" },
              { value: 'last_7d', label: '7 derniers jours' },
              { value: 'last_30d', label: '30 derniers jours' },
              { value: 'maximum', label: 'Toute la période' }
            ]}
          />
          <Button icon={<ReloadOutlined />} onClick={actualiserCampagnesEtPerformances} loading={loading}>
            Actualiser
          </Button>
        </Space>
      }
    >
      {isMobile ? (
        /* Mobile : cartes avec section « Voir les détails » pour les performances */
        <ColumnsCardList
          columns={colonnes}
          dataSource={campaigns}
          loading={loading}
          headerKeys={['name']}
          actionKeys={['actions']}
          expandedRowRender={rendreDetailsCampagne}
          clientPageSize={10}
          emptyText="Aucune campagne créée pour le moment"
        />
      ) : (
      <Table
        rowKey="id"
        loading={loading}
        dataSource={campaigns}
        columns={colonnes}
        pagination={{ pageSize: 10 }}
        locale={{ emptyText: <Empty description="Aucune campagne créée pour le moment" /> }}
        expandable={{
          // Les performances chargées s'affichent dans la ligne dépliée
          expandedRowRender: rendreDetailsCampagne
        }}
      />
      )}
      <Modal
        title={preview ? `Aperçu : ${preview.ad.name || preview.ad.id}` : 'Aperçu de la publicité'}
        open={Boolean(preview)}
        footer={null}
        onCancel={() => setPreview(null)}
        width={760}
      >
        {preview?.loading && <div className="text-gray-500">Chargement de l’aperçu…</div>}
        {!preview?.loading && preview?.brouillonAnnonce?.format === 'carousel' && (
          <div className="grid grid-cols-2 gap-3">
            {(preview.brouillonAnnonce.carouselCards || []).map((card, index) => (
              <div key={`${card.imageUrl}-${index}`} className="rounded border border-gray-200 p-2">
                {card.imageUrl && <img src={card.imageUrl} alt={card.headline || `Carte ${index + 1}`} className="w-full aspect-square object-cover rounded" />}
                <div className="mt-2 text-sm font-medium">{card.headline}</div>
              </div>
            ))}
          </div>
        )}
        {!preview?.loading && preview?.brouillonAnnonce?.format === 'slideshow' && (
          <div>
            {preview.mediaMeta?.video?.source
              ? <video controls className="w-full rounded" poster={preview.mediaMeta.video.thumbnailUrl || preview.brouillonAnnonce.slideshowImages?.[0]} src={preview.mediaMeta.video.source} />
              : <img src={preview.mediaMeta?.thumbnailUrl || preview.brouillonAnnonce.slideshowImages?.[0]} alt="Aperçu du slideshow" className="w-full rounded" />}
          </div>
        )}
        {!preview?.loading && !['carousel', 'slideshow'].includes(preview?.brouillonAnnonce?.format) && (
          <img src={preview?.brouillonAnnonce?.imageUrl || preview?.mediaMeta?.thumbnailUrl} alt="Aperçu de la publicité" className="w-full max-h-[560px] object-contain rounded" />
        )}
      </Modal>
    </Card>
  );
};

export default MetaAdsCampaignsList;
